import React, { useState, useEffect } from 'react';
import { Sparkles, Loader2, Languages, Plus, Trash2, ChevronDown, ChevronUp, ImageIcon, Check, ClipboardCopy, Wand2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { useLang, pick } from '@/lib/i18n';
import { api, errMsg } from '@/lib/api';
import { SECTION_DEFS } from '@/lib/sectionDefs';
import { SECTION_MOTIONS } from '@/lib/siteTheme';
import { FileUploadButton } from '@/components/FileUpload';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';

const AI_PRESETS = [
  { key: 'persuasive', id: 'Buat lebih persuasif dan menjual', en: 'Make it more persuasive and sales-oriented' },
  { key: 'shorten', id: 'Persingkat menjadi lebih ringkas', en: 'Shorten and make it concise' },
  { key: 'expand', id: 'Perluas dengan detail yang relevan', en: 'Expand with relevant details' },
  { key: 'formal', id: 'Buat lebih formal dan profesional', en: 'Make it more formal and professional' },
  { key: 'friendly', id: 'Buat lebih ramah dan hangat', en: 'Make it friendlier and warmer' },
  { key: 'fix', id: 'Perbaiki tata bahasa dan ejaan', en: 'Fix grammar and spelling' },
];

/** AI action menu for a single text value in a given language. */
const AiTextMenu = ({ siteId, value, fieldLang, onResult, otherLangValue, otherLang }) => {
  const { t, lang } = useLang();
  const [busy, setBusy] = useState(false);
  const run = async (instruction) => {
    if (!value?.trim()) return toast.error(lang === 'id' ? 'Teks masih kosong' : 'Text is empty');
    setBusy(true);
    try {
      const { data } = await api.post(`/sites/${siteId}/ai/rewrite`, { text: value, instruction, lang: fieldLang });
      onResult(data.text);
      toast.success(lang === 'id' ? 'Teks diperbarui oleh AI' : 'Text updated by AI');
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const translateFromOther = async () => {
    if (!otherLangValue?.trim()) return toast.error(lang === 'id' ? `Teks ${otherLang.toUpperCase()} kosong` : `${otherLang.toUpperCase()} text is empty`);
    setBusy(true);
    try {
      const payload = { text: { [otherLang]: otherLangValue, [fieldLang]: '' } };
      const { data } = await api.post(`/sites/${siteId}/ai/translate`, { payload, source_lang: otherLang, target_lang: fieldLang });
      onResult(data.payload?.text?.[fieldLang] || '');
      toast.success(lang === 'id' ? 'Diterjemahkan' : 'Translated');
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-medium text-[hsl(var(--pillot-mint))] hover:bg-secondary transition-colors" data-testid="ai-text-menu" disabled={busy}>
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} AI
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {AI_PRESETS.map((p) => <DropdownMenuItem key={p.key} onClick={() => run(lang === 'id' ? p.id : p.en)} data-testid={`ai-preset-${p.key}`}>{lang === 'id' ? p.id : p.en}</DropdownMenuItem>)}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={translateFromOther} data-testid="ai-translate-field"><Languages size={14} className="mr-2" />{t('translate')} {otherLang.toUpperCase()} → {fieldLang.toUpperCase()}</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const BilField = ({ field, value, onChange, fieldLang, siteId, testid }) => {
  const v = value || { id: '', en: '' };
  const other = fieldLang === 'id' ? 'en' : 'id';
  const set = (txt) => onChange({ ...v, [fieldLang]: txt });
  const { lang } = useLang();
  const common = { value: v[fieldLang] || '', onChange: (e) => set(e.target.value), className: 'rounded-lg text-sm', 'data-testid': `${testid}-${fieldLang}` };
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <Label className="text-xs text-muted-foreground">{pick(field.label, lang)} <span className="uppercase text-[10px] opacity-60">({fieldLang})</span></Label>
        <AiTextMenu siteId={siteId} value={v[fieldLang]} fieldLang={fieldLang} onResult={set} otherLangValue={v[other]} otherLang={other} />
      </div>
      {field.type === 'bil-textarea' ? <Textarea rows={field.rows || 3} {...common} /> : <Input {...common} />}
    </div>
  );
};

const BilLines = ({ field, value, onChange, fieldLang }) => {
  const { lang } = useLang();
  const lines = (value || []).map((x) => (typeof x === 'string' ? { id: x, en: x } : x));
  const text = lines.map((l) => l[fieldLang] || '').join('\n');
  const update = (txt) => {
    const arr = txt.split('\n');
    const next = arr.map((t, i) => ({ ...(lines[i] || { id: '', en: '' }), [fieldLang]: t }));
    onChange(next);
  };
  return (
    <div className="space-y-1">
      <Label className="text-xs text-muted-foreground">{pick(field.label, lang)} <span className="uppercase text-[10px] opacity-60">({fieldLang})</span></Label>
      <Textarea rows={4} value={text} onChange={(e) => update(e.target.value)} className="rounded-lg text-sm" />
    </div>
  );
};

const PlainField = ({ field, value, onChange, testid }) => {
  const { lang } = useLang();
  const label = <Label className="text-xs text-muted-foreground">{pick(field.label, lang)}</Label>;
  if (field.type === 'bool') {
    return <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2">{label}<Switch checked={!!value} onCheckedChange={onChange} data-testid={testid} /></div>;
  }
  if (field.type === 'select') {
    const optLabel = (o) => (field.optionLabels?.[o] ? pick(field.optionLabels[o], lang) : o);
    return (
      <div className="space-y-1">{label}
        <Select value={value || field.options[0]} onValueChange={onChange}><SelectTrigger className="rounded-lg h-9 text-sm" data-testid={testid}><SelectValue /></SelectTrigger>
          <SelectContent className="max-h-64">{field.options.map((o) => <SelectItem key={o} value={o}>{optLabel(o)}</SelectItem>)}</SelectContent></Select>
      </div>
    );
  }
  if (field.type === 'image') {
    return (
      <div className="space-y-1.5">{label}
        <div className="flex gap-2">
          <Input value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder="https://..." className="rounded-lg text-sm" data-testid={testid} />
          <FileUploadButton kind="website" imagesOnly size="sm" className="shrink-0 h-9" testid={`${testid}-upload`} onUploaded={(att) => onChange(att.url)} />
        </div>
        <div className="aspect-video w-full overflow-hidden rounded-lg border border-border bg-muted flex items-center justify-center">
          {value ? <img src={value} alt="" className="h-full w-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} /> : <ImageIcon className="text-muted-foreground" />}
        </div>
      </div>
    );
  }
  return <div className="space-y-1">{label}<Input value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} className="rounded-lg text-sm" data-testid={testid} /></div>;
};

/** Multi-select of the tenant's sellable Inventory products (for product_featured). */
const ProductsField = ({ field, value, onChange, testid }) => {
  const { lang } = useLang();
  const { company } = useAuth();
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState('');
  useEffect(() => { if (!company?.id) return; api.get(`/companies/${company.id}/inventory/products`).then(({ data }) => setRows((data.products || data || []).filter((p) => p.active !== false && p.sell_ok !== false && p.ecommerce_ok !== false))).catch(() => setRows([])); }, [company?.id]);
  const sel = Array.isArray(value) ? value : [];
  const max = field.max || 3;
  const toggle = (id) => onChange(sel.includes(id) ? sel.filter((x) => x !== id) : sel.length >= max ? sel : [...sel, id]);
  const list = (rows || []).filter((p) => !q || p.name.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-muted-foreground">{pick(field.label, lang)} · {sel.length}/{max}</Label>
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={lang === 'id' ? 'Cari produk…' : 'Search products…'} className="rounded-lg h-8 text-sm" data-testid={`${testid}-search`} />
      <div className="max-h-52 overflow-y-auto rounded-lg border border-border divide-y divide-border" data-testid={testid}>
        {rows === null ? <div className="p-3 text-xs text-muted-foreground">…</div> : list.length === 0 ? <div className="p-3 text-xs text-muted-foreground">{lang === 'id' ? 'Tidak ada produk.' : 'No products.'}</div>
          : list.map((p) => { const on = sel.includes(p.id); return (
            <button type="button" key={p.id} onClick={() => toggle(p.id)} disabled={!on && sel.length >= max} className={`flex w-full items-center gap-2 px-2.5 py-2 text-left text-xs transition-colors disabled:opacity-40 ${on ? 'bg-secondary/60' : 'hover:bg-secondary/30'}`} data-testid={`${testid}-${p.id}`}>
              <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${on ? 'border-primary bg-primary text-white' : 'border-border'}`}>{on && <Check size={10} />}</span>
              {p.image_url ? <img src={p.image_url} alt="" className="h-7 w-7 rounded object-cover" /> : <span className="h-7 w-7 rounded bg-muted" />}
              <span className="min-w-0 flex-1 truncate">{p.name}</span><span className="text-muted-foreground">{p.qty_on_hand != null ? `stok ${Math.round(p.qty_on_hand)}` : ''}</span>
            </button>); })}
      </div>
    </div>
  );
};

const EmailListsField = ({ field, value, onChange, testid }) => {
  const { lang } = useLang();
  const { company } = useAuth();
  const [rows, setRows] = useState(null);
  useEffect(() => { if (!company?.id) return; api.get(`/companies/${company.id}/marketing/lists`).then(({ data }) => setRows((data.items || []).filter((l) => l.is_public))).catch(() => setRows([])); }, [company?.id]);
  const sel = Array.isArray(value) ? value : [];
  const toggle = (id) => onChange(sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]);
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-muted-foreground">{pick(field.label, lang)} · {sel.length}</Label>
      <div className="max-h-40 overflow-y-auto rounded-lg border border-border divide-y divide-border" data-testid={testid}>
        {rows === null && <div className="px-2 py-2 text-xs text-muted-foreground">…</div>}
        {rows && rows.length === 0 && <div className="px-2 py-2 text-xs text-muted-foreground">{lang === 'id' ? 'Belum ada milis publik. Buat di modul Pemasaran Email → Milis (aktifkan "Milis publik").' : 'No public lists yet. Create one in Email Marketing → Lists.'}</div>}
        {(rows || []).map((l) => <label key={l.id} className="flex cursor-pointer items-center gap-2 px-2 py-1.5 text-xs hover:bg-muted"><input type="checkbox" checked={sel.includes(l.id)} onChange={() => toggle(l.id)} data-testid={`${testid}-${l.id}`} /><span className="flex-1 truncate">{l.name}</span><span className="text-muted-foreground">{l.active_count}</span></label>)}
      </div>
    </div>
  );
};

const Field = ({ field, value, onChange, fieldLang, siteId, testid }) => {
  if (field.type === 'email_lists') return <EmailListsField field={field} value={value} onChange={onChange} testid={testid} />;
  if (field.type === 'products') return <ProductsField field={field} value={value} onChange={onChange} testid={testid} />;
  if (field.type === 'bil-text' || field.type === 'bil-textarea') return <BilField field={field} value={value} onChange={onChange} fieldLang={fieldLang} siteId={siteId} testid={testid} />;
  if (field.type === 'bil-lines') return <BilLines field={field} value={value} onChange={onChange} fieldLang={fieldLang} />;
  return <PlainField field={field} value={value} onChange={onChange} testid={testid} />;
};

const ListField = ({ field, value, onChange, fieldLang, siteId }) => {
  const { lang } = useLang();
  const items = Array.isArray(value) ? value : [];
  const [openIdx, setOpenIdx] = useState(0);
  const setItem = (i, patch) => onChange(items.map((it, j) => (j === i ? { ...it, ...patch } : it)));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
    setOpenIdx(j);
  };
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between"><Label className="text-xs text-muted-foreground">{pick(field.label, lang)} ({items.length})</Label>
        <Button type="button" size="sm" variant="outline" className="h-7 rounded-lg text-xs" onClick={() => { onChange([...items, field.newItem()]); setOpenIdx(items.length); }} data-testid={`inspector-list-add-${field.key}`}><Plus size={12} className="mr-1" />{lang === 'id' ? 'Tambah' : 'Add'}</Button></div>
      {items.map((it, i) => (
        <div key={i} className="rounded-lg border border-border bg-white">
          <button type="button" onClick={() => setOpenIdx(openIdx === i ? -1 : i)} className="flex w-full items-center justify-between px-3 py-2 text-left text-sm">
            <span className="truncate font-medium">{i + 1}. {pick(it[field.itemLabel], fieldLang) || (typeof it[field.itemLabel] === 'string' ? it[field.itemLabel] : '') || '-'}</span>
            {openIdx === i ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          {openIdx === i && (
            <div className="space-y-3 border-t border-border p-3">
              {field.itemFields.map((f) => <Field key={f.key} field={f} value={it[f.key]} onChange={(v) => setItem(i, { [f.key]: v })} fieldLang={fieldLang} siteId={siteId} testid={`inspector-${field.key}-${i}-${f.key}`} />)}
              <div className="flex justify-between pt-1">
                <div className="flex gap-1">
                  <Button type="button" size="sm" variant="ghost" className="h-7 px-2" onClick={() => move(i, -1)}><ChevronUp size={14} /></Button>
                  <Button type="button" size="sm" variant="ghost" className="h-7 px-2" onClick={() => move(i, 1)}><ChevronDown size={14} /></Button>
                </div>
                <Button type="button" size="sm" variant="ghost" className="h-7 text-destructive hover:text-destructive" onClick={() => onChange(items.filter((_, j) => j !== i))} data-testid={`inspector-list-remove-${field.key}-${i}`}><Trash2 size={14} /></Button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export const SectionInspector = ({ section, onChange, siteId, onTranslateSection, translating, onDelete, onDuplicate, onCopyToPage }) => {
  const { t, lang } = useLang();
  const [fieldLang, setFieldLang] = useState(lang);
  const def = SECTION_DEFS[section.type];
  if (!def) return <div className="p-4 text-sm text-muted-foreground">Unknown section</div>;
  const content = section.content || {};
  const setContent = (key, val) => onChange({ ...section, content: { ...content, [key]: val } });
  const other = fieldLang === 'id' ? 'en' : 'id';
  return (
    <div className="flex h-full flex-col" data-testid="section-inspector">
      <div className="border-b border-border px-4 py-3">
        <div className="flex items-center justify-between">
          <div><div className="text-[11px] uppercase tracking-wide text-muted-foreground">{t('sections')}</div><div className="font-display font-semibold">{pick(def.label, lang)}</div></div>
          <Tabs value={fieldLang} onValueChange={setFieldLang}>
            <TabsList className="h-8 rounded-lg" data-testid="inspector-lang-tabs"><TabsTrigger value="id" className="h-6 rounded-md px-2.5 text-xs" data-testid="inspector-lang-id">ID</TabsTrigger><TabsTrigger value="en" className="h-6 rounded-md px-2.5 text-xs" data-testid="inspector-lang-en">EN</TabsTrigger></TabsList>
          </Tabs>
        </div>
        <Button type="button" size="sm" variant="outline" className="mt-3 w-full rounded-lg text-xs" disabled={translating} onClick={() => onTranslateSection(other, fieldLang)} data-testid="inspector-translate-section">
          {translating ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <Languages size={13} className="mr-1.5" />}{lang === 'id' ? `Terjemahkan section ${other.toUpperCase()} → ${fieldLang.toUpperCase()} (AI)` : `Translate section ${other.toUpperCase()} → ${fieldLang.toUpperCase()} (AI)`}
        </Button>
      </div>
      <div className="flex-1 space-y-4 overflow-y-auto p-4 scrollbar-thin">
        <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">{lang === 'id' ? 'Anchor (ID untuk link menu, mis. hubungi-kami)' : 'Anchor (ID for menu links, e.g. contact-us)'}</Label><div className="flex items-center gap-1"><span className="text-sm text-muted-foreground">#</span><Input value={section.anchor || ''} onChange={(e) => onChange({ ...section, anchor: e.target.value.toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/^-|-$/g, '') })} placeholder="tentang-kami" className="rounded-xl h-9 text-sm font-mono" data-testid="inspector-anchor" /></div></div>
        <div className="rounded-xl border border-border bg-secondary/30 p-3 space-y-2" data-testid="inspector-style-group">
          <div className="flex items-center gap-1.5 text-xs font-semibold"><Wand2 size={13} className="text-[hsl(var(--pillot-mint))]" />{lang === 'id' ? 'Animasi saat scroll' : 'Scroll animation'}</div>
          <Select value={section.style?.motion || 'inherit'} onValueChange={(v) => onChange({ ...section, style: { ...(section.style || {}), motion: v } })}>
            <SelectTrigger className="rounded-lg h-9 text-sm bg-white" data-testid="inspector-motion"><SelectValue /></SelectTrigger>
            <SelectContent>{SECTION_MOTIONS.map((o) => <SelectItem key={o.v} value={o.v} data-testid={`inspector-motion-${o.v}`}>{lang === 'id' ? o.id : o.en}</SelectItem>)}</SelectContent>
          </Select>
          <p className="text-[11px] text-muted-foreground">{lang === 'id' ? 'Preset animasi seluruh situs diatur di Pengaturan → Tema.' : 'The site-wide animation preset lives in Settings → Theme.'}</p>
        </div>
        {def.fields.map((f) => f.type === 'list'
          ? <ListField key={f.key} field={f} value={content[f.key]} onChange={(v) => setContent(f.key, v)} fieldLang={fieldLang} siteId={siteId} />
          : <Field key={f.key} field={f} value={content[f.key]} onChange={(v) => setContent(f.key, v)} fieldLang={fieldLang} siteId={siteId} testid={`inspector-${f.key}`} />)}
        {(onDelete || onDuplicate) && (
          <div className="flex gap-2 border-t border-border pt-4" data-testid="inspector-actions">
            <Button type="button" size="sm" variant="outline" className="flex-1 rounded-lg text-xs" onClick={() => onChange({ ...section, hidden: !section.hidden })} data-testid="inspector-toggle-hidden">{section.hidden ? (lang === 'id' ? 'Tampilkan' : 'Show') : (lang === 'id' ? 'Sembunyikan' : 'Hide')}</Button>
            {onDuplicate && <Button type="button" size="sm" variant="outline" className="flex-1 rounded-lg text-xs" onClick={onDuplicate} data-testid="inspector-duplicate"><Plus size={13} className="mr-1" />{lang === 'id' ? 'Duplikat' : 'Duplicate'}</Button>}
            {onCopyToPage && <Button type="button" size="sm" variant="outline" className="flex-1 rounded-lg text-xs" onClick={onCopyToPage} data-testid="inspector-copy-to-page"><ClipboardCopy size={13} className="mr-1" />{lang === 'id' ? 'Salin ke hal.' : 'Copy to page'}</Button>}
            {onDelete && <Button type="button" size="sm" variant="destructive" className="flex-1 rounded-lg text-xs" onClick={onDelete} data-testid="inspector-delete"><Trash2 size={13} className="mr-1" />{lang === 'id' ? 'Hapus section' : 'Delete section'}</Button>}
          </div>)}
      </div>
    </div>
  );
};
